import { LockingScript, P2PKH, PrivateKey, Script, Transaction, fromUtxo } from '@treechat/bsv-sdk'
import {
    type OrdinalPrevoutScript,
    discardOrdinalPrevoutScript,
    signOrdinalPrevoutInput,
} from './ordinal-prevout-signing'

const MAX_SCRIPT_BYTES = 8 * 1024 * 1024
const HEX_32_BYTES = /^[0-9a-f]{64}$/i

export type OrdinalTransferInput = {
    txid: string
    vout: number
    satoshis: number
    scriptPubKey?: string
    scriptSha256: string
    role: 'ordinal' | 'payment'
    sequence: number
}

export type OrdinalTransferOutput = {
    script: string
    satoshis: number
}

/** The browser-signing payload. Serialize only this object for its digest. */
export type OrdinalTransferManifest = {
    version: 1
    signingRequestId: string
    attemptId: string
    userId: string
    deviceId: string
    /** SHA-256 hex fingerprint of `${paymentAddress}:${ordinalsAddress}`. */
    walletGeneration: string
    paymentAddress: string
    ordinalsAddress: string
    recipientAddress: string
    ordinal: { txid: string; vout: number }
    inputs: OrdinalTransferInput[]
    outputs: OrdinalTransferOutput[]
    txVersion: number
    lockTime: number
    feeSatoshis: number
    recipientOrdinalVout: number
}

export type OrdinalTransferSignature = {
    manifestDigest: string
    inputIndex: number
    txid: string
    vout: number
    unlockingScriptHex: string
}

export type OrdinalTransferSigningRequest = {
    manifest: OrdinalTransferManifest
    /** Streamed separately so the full ordinal script never enters the manifest or memory. */
    ordinalPrevoutScript: OrdinalPrevoutScript
    payPk: PrivateKey | string
    ordPk: PrivateKey | string
    expectedUserId: string
    expectedDeviceId: string
}

function canonicalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonicalize)
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
                .map(([key, entry]) => [key, canonicalize(entry)]),
        )
    }
    return value
}

function bytesToHex(bytes: ArrayBuffer): string {
    return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('')
}

async function sha256Hex(value: string): Promise<string> {
    if (!globalThis.crypto?.subtle) throw new Error('WebCrypto SHA-256 is unavailable')
    return bytesToHex(
        await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)),
    )
}

/** SHA-256 of recursive-key-sorted JSON, shared with the server-side verifier. */
export async function ordinalManifestDigest(manifest: OrdinalTransferManifest): Promise<string> {
    return sha256Hex(JSON.stringify(canonicalize(manifest)))
}

function requireInteger(name: string, value: number, minimum = 0): void {
    if (!Number.isSafeInteger(value) || value < minimum)
        throw new Error(`${name} must be a safe integer >= ${minimum}`)
}

function requireHex(name: string, value: string): void {
    if (!value || value.length % 2 !== 0 || !/^[0-9a-f]+$/i.test(value))
        throw new Error(`${name} must be hexadecimal`)
}

function requireTxid(name: string, value: string): void {
    if (!HEX_32_BYTES.test(value)) throw new Error(`${name} must be a 32-byte transaction id`)
}

function normalizedHex(value: string): string {
    return value.toLowerCase()
}

function expectedP2PKHScript(address: string): string {
    return new P2PKH().lock(address).toHex()
}

function keyFor(value: PrivateKey | string): PrivateKey {
    return typeof value === 'string' ? PrivateKey.fromWif(value) : value
}

function assertKeyAddress(name: string, key: PrivateKey, expectedAddress: string): void {
    if (key.toAddress() !== expectedAddress)
        throw new Error(`${name} does not derive ${expectedAddress}`)
}

function assertP2PKHTail(script: Script, expectedAddress: string, name: string): void {
    if (normalizedHex(script.toHex()) !== normalizedHex(expectedP2PKHScript(expectedAddress))) {
        throw new Error(`${name} must use the expected P2PKH locking script`)
    }
}

function scriptFromHex(name: string, hex: string): LockingScript {
    requireHex(name, hex)
    if (hex.length / 2 > MAX_SCRIPT_BYTES)
        throw new Error(`${name} exceeds ${MAX_SCRIPT_BYTES} bytes`)
    return LockingScript.fromHex(hex)
}

async function assertScriptHash(
    name: string,
    scriptHex: string,
    expectedHash: string,
): Promise<void> {
    if (!HEX_32_BYTES.test(expectedHash))
        throw new Error(`${name} scriptSha256 must be a SHA-256 hex digest`)
    const bytes = Uint8Array.from(scriptHex.match(/../g)!.map(byte => Number.parseInt(byte, 16)))
    const actualHash = bytesToHex(await globalThis.crypto.subtle.digest('SHA-256', bytes))
    if (actualHash !== normalizedHex(expectedHash))
        throw new Error(`${name} script hash does not match the manifest`)
}

function validateManifest(manifest: OrdinalTransferManifest): void {
    if (manifest.version !== 1) throw new Error('unsupported ordinal signing manifest version')
    if (
        !manifest.signingRequestId ||
        !manifest.attemptId ||
        !manifest.userId ||
        !manifest.deviceId
    ) {
        throw new Error('ordinal signing manifest identity fields are required')
    }
    if (!HEX_32_BYTES.test(manifest.walletGeneration))
        throw new Error('walletGeneration must be a SHA-256 hex digest')
    requireInteger('txVersion', manifest.txVersion)
    requireInteger('lockTime', manifest.lockTime)
    requireInteger('feeSatoshis', manifest.feeSatoshis)
    requireInteger('recipientOrdinalVout', manifest.recipientOrdinalVout)
    if (manifest.inputs.length < 1 || manifest.outputs.length < 1)
        throw new Error('ordinal signing manifest needs inputs and outputs')
    if (manifest.recipientOrdinalVout !== 0)
        throw new Error('recipient ordinal output must be vout 0')
    const ordinalInput = manifest.inputs[0]
    if (!ordinalInput || ordinalInput.role !== 'ordinal' || ordinalInput.satoshis !== 1) {
        throw new Error('input 0 must be exactly one 1-satoshi ordinal input')
    }
    if (
        ordinalInput.txid !== manifest.ordinal.txid ||
        ordinalInput.vout !== manifest.ordinal.vout
    ) {
        throw new Error('ordinal input must match the declared ordinal outpoint')
    }
    requireTxid('ordinal.txid', manifest.ordinal.txid)
    manifest.inputs.forEach((input, index) => {
        requireTxid(`inputs[${index}].txid`, input.txid)
        requireInteger(`inputs[${index}].vout`, input.vout)
        requireInteger(`inputs[${index}].satoshis`, input.satoshis, 1)
        requireInteger(`inputs[${index}].sequence`, input.sequence)
        if (!HEX_32_BYTES.test(input.scriptSha256))
            throw new Error(`inputs[${index}].scriptSha256 is invalid`)
        if (index === 0 ? input.role !== 'ordinal' : input.role !== 'payment') {
            throw new Error('inputs must be ordered ordinal first, then payment inputs')
        }
        if (index > 0 && !input.scriptPubKey)
            throw new Error(`payment input ${index} is missing scriptPubKey`)
    })
    const recipientOutput = manifest.outputs[0]
    if (
        recipientOutput.satoshis !== 1 ||
        normalizedHex(recipientOutput.script) !==
            normalizedHex(expectedP2PKHScript(manifest.recipientAddress))
    ) {
        throw new Error('output 0 must send exactly one satoshi to recipientAddress')
    }
    manifest.outputs.forEach((output, index) => {
        requireInteger(`outputs[${index}].satoshis`, output.satoshis, index === 0 ? 0 : 1)
        requireHex(`outputs[${index}].script`, output.script)
        if (
            index > 0 &&
            normalizedHex(output.script) !==
                normalizedHex(expectedP2PKHScript(manifest.paymentAddress))
        ) {
            throw new Error('non-ordinal outputs must be P2PKH payment change')
        }
    })
    const inputsTotal = manifest.inputs.reduce((sum, input) => sum + input.satoshis, 0)
    const outputsTotal = manifest.outputs.reduce((sum, output) => sum + output.satoshis, 0)
    if (inputsTotal - outputsTotal !== manifest.feeSatoshis)
        throw new Error('manifest fee does not equal inputs minus outputs')
    // A send carries no inscription bytes in the new transaction. Reject an
    // unexpectedly large miner fee even when a server-prepared template is
    // internally consistent, without adding another approval step.
    const estimatedBytes = 10 + manifest.inputs.length * 180 + manifest.outputs.length * 34
    const maximumFeeSatoshis = Math.max(1000, estimatedBytes * 20)
    if (manifest.feeSatoshis > maximumFeeSatoshis) {
        throw new Error('ordinal send fee exceeds the local safety limit')
    }
}

/**
 * Signs one already-validated ordinal transfer. The transaction object is intentionally local and
 * discarded after its per-input scripts are extracted; no transaction hex is returned or retained.
 */
export async function signOrdinalTransfer(
    request: OrdinalTransferSigningRequest,
): Promise<OrdinalTransferSignature[]> {
    try {
        return await signValidatedOrdinalTransfer(request)
    } finally {
        discardOrdinalPrevoutScript(request.ordinalPrevoutScript)
    }
}

async function signValidatedOrdinalTransfer(
    request: OrdinalTransferSigningRequest,
): Promise<OrdinalTransferSignature[]> {
    const { manifest } = request
    validateManifest(manifest)
    if (
        manifest.userId !== request.expectedUserId ||
        manifest.deviceId !== request.expectedDeviceId
    ) {
        throw new Error('ordinal signing manifest is for a different user or device')
    }
    const expectedWalletGeneration = await sha256Hex(
        `${manifest.paymentAddress}:${manifest.ordinalsAddress}`,
    )
    if (normalizedHex(manifest.walletGeneration) !== expectedWalletGeneration) {
        throw new Error(
            'ordinal signing manifest walletGeneration does not match its wallet addresses',
        )
    }
    const payPk = keyFor(request.payPk)
    const ordPk = keyFor(request.ordPk)
    assertKeyAddress('payPk', payPk, manifest.paymentAddress)
    assertKeyAddress('ordPk', ordPk, manifest.ordinalsAddress)

    const tx = new Transaction(manifest.txVersion, [], [], manifest.lockTime)
    for (const output of manifest.outputs) {
        tx.addOutput({
            satoshis: output.satoshis,
            lockingScript: scriptFromHex('output script', output.script),
        })
    }

    for (const [index, input] of manifest.inputs.entries()) {
        if (index === 0) {
            // Signed below from the streamed prevout; the SDK only signs payments.
            tx.addInput({
                sourceTXID: input.txid,
                sourceOutputIndex: input.vout,
                sequence: input.sequence,
            })
            continue
        }
        const lockingScript = scriptFromHex(
            `payment input ${index} scriptPubKey`,
            input.scriptPubKey!,
        )
        assertP2PKHTail(lockingScript, manifest.paymentAddress, `payment input ${index}`)
        await assertScriptHash(`input ${index}`, lockingScript.toHex(), input.scriptSha256)
        const txInput = fromUtxo(
            {
                txid: input.txid,
                vout: input.vout,
                satoshis: input.satoshis,
                script: lockingScript.toHex(),
            },
            new P2PKH().unlock(payPk, 'all', false, input.satoshis, lockingScript),
        )
        txInput.sequence = input.sequence
        tx.addInput(txInput)
    }

    const ordinalUnlockingScriptHex = await signOrdinalPrevoutInput({
        tx: manifest,
        inputIndex: 0,
        satoshis: manifest.inputs[0].satoshis,
        script: request.ordinalPrevoutScript,
        expectedScriptSha256: manifest.inputs[0].scriptSha256,
        ordinalsAddress: manifest.ordinalsAddress,
        allowPlainP2PKH: false,
        key: ordPk,
    })
    await tx.sign()
    const manifestDigest = await ordinalManifestDigest(manifest)
    return manifest.inputs.map((input, inputIndex) => {
        const unlockingScriptHex =
            inputIndex === 0
                ? ordinalUnlockingScriptHex
                : tx.inputs[inputIndex].unlockingScript?.toHex()
        if (!unlockingScriptHex) throw new Error(`input ${inputIndex} was not signed`)
        return {
            manifestDigest,
            inputIndex,
            txid: input.txid,
            vout: input.vout,
            unlockingScriptHex,
        }
    })
}
